import { endOfDay, startOfYear, subDays, subMonths, subWeeks, subYears } from 'date-fns';
import { useMemo } from 'react';

export type ChartRangeItemId = '1d' | '1w' | '1m' | '3m' | '6m' | 'ytd' | '1y' | '3y' | '5y' | 'max';

export type ChartRangeItem = {
  id: ChartRangeItemId;
  label: string;
  start: Date;
  end: Date;
};

export function useChartRanges(
  today: Date,
  rangeIds: ChartRangeItemId[],
  labels: Partial<Record<ChartRangeItemId, string>>,
): ChartRangeItem[] {
  return useMemo((): ChartRangeItem[] => {
    // We can't get data from the future, but this leaves room for error in case our/servers time is not in sync
    const end = endOfDay(today);

    const allRanges: Record<ChartRangeItemId, ChartRangeItem> = {
      '1d': { id: '1d', start: subDays(today, 1), end, label: labels['1d'] ?? '' },
      '1w': { id: '1w', start: subWeeks(today, 1), end, label: labels['1w'] ?? '' },
      '1m': { id: '1m', start: subMonths(today, 1), end, label: labels['1m'] ?? '' },
      '3m': { id: '3m', start: subMonths(today, 3), end, label: labels['3m'] ?? '' },
      '6m': { id: '6m', start: subMonths(today, 6), end, label: labels['6m'] ?? '' },
      ytd: { id: 'ytd', start: startOfYear(today), end, label: labels['ytd'] ?? '' },
      '1y': { id: '1y', start: subYears(today, 1), end, label: labels['1y'] ?? '' },
      '3y': { id: '3y', start: subYears(today, 3), end, label: labels['3y'] ?? '' },
      '5y': { id: '5y', start: subYears(today, 5), end, label: labels['5y'] ?? '' },
      max: { id: 'max', start: subYears(today, 20), end, label: labels['max'] ?? '' },
    };

    return rangeIds.map(rangeId => allRanges[rangeId]);
  }, [labels, rangeIds, today]);
}
